import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect } from 'react';
import { useTheme } from '@amzn/meridian/theme';
import { rio, rioDark } from '@amzn/rio-design-tokens';
import { ThemeProvider } from '@emotion/react';
import { Outlet, useSearchParams, useLocation, matchPath } from 'react-router';
import { FetchError } from '@amzn/handshake-painter-typescript-client/v1';
import { AlertProvider } from './common/alert';
import { HeaderProvider } from './common/header/HeaderContext';
import { AppError } from './common/errors';
import { Footer } from './common/footer';
import { Header } from './common/header';
import { BouncingBox } from './common/loaders';
import { useAppInit } from './hooks';
import { useOnAppInitSuccess } from './hooks/useOnAppInitSuccess';
import { getExperimentArm } from './utils/experiments';
import { redirectToLegacyPainter } from './utils/redirectUtils';
import { queryClient } from './api/queryClient';
import { isRouteSupported, routes } from './utils/routes';
import { ParzivalGatewayAuthErrorType } from './utils/auth';
import { getMarketplace } from './utils/marketplace';
import { getMobileOperatingSystem, Platform } from './utils/user';
import Column from '@amzn/meridian/column';
// TODO: Remove after full migration
const shouldRedirect = (searchParams, pathname) => {
    const marketplace = getMarketplace(searchParams);
    const ascsubtag = searchParams.get('ascsubtag');
    // Redirect unrecognized traffic (missing "tag" param) to legacy painter
    if (!searchParams.has('tag')) {
        return 'unrecognized_traffic';
        // Redirect non-US marketplaces to legacy painter
    }
    else if (marketplace !== 'US') {
        return 'unsupported_marketplace';
        // TODO: Temporary hack - Redirect Android users with psc ascsubtag to legacy painter until Snapchat fixes their Android setup
    }
    else if (getMobileOperatingSystem() === Platform.ANDROID && (ascsubtag === null || ascsubtag === void 0 ? void 0 : ascsubtag.startsWith('psc'))) {
        return 'unsupported_platform';
        // For unsupported routes, we want to redirect without showing the Painter 2.0 bouncing box
    }
    else if (!isRouteSupported(pathname)) {
        return 'unsupported_route';
    }
    return undefined;
};
export const App = () => {
    /**
     * The ThemeProvider from @emotion/react allows us to pass in the rio tokens and then access
     * them in the @emotion/styled components. The "mode" from "useTheme" is set in index.tsx
     */
    const { mode } = useTheme();
    const theme = mode === 'light' ? rio : rioDark;
    const location = useLocation();
    const [searchParams] = useSearchParams();
    // TODO: Remove hasStayParam check after full migration
    // The hasStayParam acts as an override so that we can test the experience on SMAs without affecting prod traffic
    const hasStayParam = searchParams.has('STAY_IN_PAINTER_2_DOT_0');
    const redirectReason = shouldRedirect(searchParams, location.pathname);
    const shouldRedirectBeforeInit = !!redirectReason && !hasStayParam;
    /**
     * DO NOT CALL useAppInit in any other file within this components. If useAppInit fails and is called
     * downstream from here, then it will try to fetch again, causing an infinite loop.
     */
    const { isError, isLoading, error, data, isSuccess } = useAppInit({ enabled: !shouldRedirectBeforeInit });
    const onAppInitSuccess = useOnAppInitSuccess();
    const painter2LaunchArm = getExperimentArm(data === null || data === void 0 ? void 0 : data.experiments, 'WEBLAB_PAINTER_2_0_LAUNCH');
    /**
     * 1. For EXPIRED_AUTH error on init, stay in 2.0
     * 2. On any other error (networking, MISSING_AUTH, etc), redirect to 1.0
     */
    const errorCases = [ParzivalGatewayAuthErrorType.EXPIRED_AUTH, ParzivalGatewayAuthErrorType.MISSING_AUTH];
    const redirectOnError = error instanceof FetchError ? !errorCases.includes(error.cause.errorType) : isError;
    const shouldAppInitRedirect = redirectOnError || (isSuccess && painter2LaunchArm === 'C' && !hasStayParam);
    /**
     * Clear the product details queries on location change. This is tied to the product selection
     * implementation where we cache this query for prefetching purposes.
     */
    useEffect(() => {
        if (!matchPath(routes.productDetailPage, location.pathname)) {
            queryClient.removeQueries({ predicate: (query) => query.queryKey.includes('getProductDetails') });
        }
    }, [location.pathname]);
    // Reset scroll on page transitions
    useEffect(() => {
        var _a;
        if (!((_a = location.state) === null || _a === void 0 ? void 0 : _a.preventScrollReset)) {
            window.scrollTo({ top: 0, behavior: 'instant' });
        }
    }, [location.pathname, location.state]);
    // AppInit success case
    useEffect(() => {
        if (isSuccess) {
            onAppInitSuccess(data);
        }
        // We perform this in a useEffect so that the BoucingBox continues to render while this logic is executing
        if (shouldAppInitRedirect) {
            redirectToLegacyPainter(redirectOnError ? 'app_init_error' : 'experiment_control_group');
        }
    }, [redirectOnError, isSuccess, shouldAppInitRedirect]);
    if (shouldRedirectBeforeInit) {
        redirectToLegacyPainter(redirectReason);
        return null;
    }
    if (isLoading || shouldAppInitRedirect) {
        return _jsx(BouncingBox, {});
    }
    return (_jsxs(ThemeProvider, { theme: { mode, rio: theme }, children: [isError && (_jsx(Column, { "data-testid": "app-error", minHeight: "100vh", spacing: "none", spacingInset: "none", backgroundColor: theme.semantic.color.background.primary, children: _jsx(AppError, { error: error }) })), !isError && (_jsx(HeaderProvider, { children: _jsxs(Column, { "data-testid": "app", minHeight: "100vh", heights: ['fit', 'fill', 'fit'], spacing: "none", spacingInset: "none", backgroundColor: theme.semantic.color.background.primary, children: [_jsx(Header, {}), _jsx(AlertProvider, { children: _jsx(Outlet, {}) }), _jsx(Footer, {})] }) }))] }));
};
